import { MutableRefObject } from "react";
import {
  Button,
  DataTableProps,
  isNullish,
  OverflowText,
  Pill,
  renderCellValue
} from "@theorgchartdev/storybook";
import { boxObjClassFieldIds, enumObjClassNames, enumOverrideType } from "@coreapp/constants";
import { posObjClassFieldIds } from "@coreapp/constants";
import { GetWSMgr, GetDoc } from "@/Utils/StateLib";
import { promptToUnassignEmployees } from "@/appComponents/Blocks/Menus/BoxContextMenu/unassignEmployee";
import { promptToReleaseEmployees } from "@/appComponents/Blocks/Menus/BoxContextMenu/releaseEmployee";
import { promptToClosePosition } from "@/appComponents/Blocks/Menus/PositionContextMenu/closePosition";
import { PillColors } from "@theorgchartdev/storybook/dist/components/Pill/Pill";
import { appstr } from "@coreapp/utils/appstr";
import {
  isBlockedField,
  getBlockedFieldDisplayValue,
  sanitizeBlockedValue
} from "@coreapp/utils/FormatFieldValue";
import { QObjDB } from "@coreapp/CoreApp/QObjDB";
import { ColumnDef, CellContext, Row, RowSelectionState } from "@tanstack/react-table";
import { usePlanningTabularContext } from "@/providers/PlanningTabularProvider";
import { loadEmployeeById } from "@/appComponents/Blocks/Modals/RecordEditorModal/PositionEditorUtils";
import { promptToReopenPosition } from "@/appComponents/Blocks/Menus/PositionContextMenu/openPosition";

export const getLocalizationMap = (): Partial<
  Record<
    (typeof enumObjClassNames)["box" | "position"],
    Record<(typeof boxObjClassFieldIds)["NAME"], string>
  >
> => ({
  [enumObjClassNames.box]: {
    [boxObjClassFieldIds.NAME]: appstr("Assignments")
  }
});

export const getRowCanExpand: DataTableProps<any>["getRowCanExpand"] = row => {
  return row.original.subRows?.length > 1;
};

export const isParentOrSubRow = (row: Row<any>): boolean => {
  return row.getCanExpand() || row.depth > 0;
};

export const getRowId: () => DataTableProps<any>["getRowId"] = () => {
  let id = -1;
  const dupeMap = new Set<string>();
  const getId = () => {
    id += 1;
    return JSON.stringify(new Date().getTime()) + `_${id}`;
  };

  return (originalRow, index, parent) => {
    if (
      (originalRow.dataType === enumObjClassNames.position ||
        originalRow.dataType === enumObjClassNames.box) &&
      originalRow._id
    ) {
      let originalId = originalRow._id;
      if (dupeMap.has(originalId)) {
        console.info(`Duplicate ID found: ${originalId}`);
        const uniqueId = getId();
        originalId = `${originalId}__(DuplicateId__${uniqueId})`;
      }
      dupeMap.add(originalId);
      return `${originalId}`;
    }
    console.info("Unknown ID");
    const uniqueId = getId();
    return `UnknownId: ${parent?.index}-${index}-${uniqueId}`;
  };
};

export const getOverflowText = (text: string) => {
  return <OverflowText className="oc-table-overflow-text">{text}</OverflowText>;
};

export const createDesiredHoldersColumn = (
  commonDef: ColumnDef<any>,
  objDb: QObjDB
): ColumnDef<any> => {
  return {
    ...commonDef,
    size: 150,
    cell: ({ row, getValue, column }: CellContext<any, string>) => {
      const holderCountFieldName = objDb
        ?.GetClassByName(enumObjClassNames.position)
        ?.GetFieldDefByID(posObjClassFieldIds.HOLDER_COUNT)
        ?.GetFieldName();
      const holderCount = Number(row.original[holderCountFieldName] || 0);

      const value = getValue();
      const desiredCount = !isNullish(value) ? Number(value) : holderCount;

      const staffingTarget = `${holderCount}/${desiredCount}`;

      const pillColors: PillColors = getPillColorsByStaffingStatus(holderCount, desiredCount);

      return renderCellValue({
        row,
        value,
        column,
        renderValue: () => <Pill label={staffingTarget} pillColors={pillColors} />
      });
    }
  };
};

const getPillColorsByStaffingStatus = (actual: number, desired: number): PillColors => {
  if (actual > desired) {
    return {
      color: "#802626",
      backgroundColor: "#FDEDEB",
      borderColor: "#D04343"
    };
  }
  if (actual < desired) {
    return {
      color: "#916903",
      backgroundColor: "#FFF8ED",
      borderColor: "#FFB700"
    };
  }
  return {
    color: "#143F1C",
    backgroundColor: "#EBF2EB",
    borderColor: "#338441"
  };
};

export const createParentPositionColumn = (
  commonDef: ColumnDef<any>,
  objDb: QObjDB
): ColumnDef<any> => {
  const posTitleFieldDef = objDb
    ?.GetClassByName(enumObjClassNames.position)
    ?.GetFieldDefByID(posObjClassFieldIds.POSITION_TITLE);
  const posTitleFieldName = posTitleFieldDef?.GetFieldName();

  return {
    ...commonDef,
    cell: ({ row, getValue, column }: CellContext<any, string>) => {
      const value = getValue()?.trim();

      if (isBlockedField(value)) {
        return getBlockedFieldDisplayValue();
      }

      const parentPosition = row.original.__PARENT__?.[0];
      const parentTitle = parentPosition?.[posTitleFieldName ?? ""];
      const sanitizedParentTitle = sanitizeBlockedValue(parentTitle);

      const renderValue =
        sanitizedParentTitle && value ? `${sanitizedParentTitle}: ${value}` : value;

      return renderCellValue({
        row,
        value,
        column,
        renderValue: () => getOverflowText(renderValue)
      });
    }
  };
};

export const createAssignmentsColumn = (
  commonDef: ColumnDef<any>,
  fieldName: string,
  toastHighlightSelectionRef?: MutableRefObject<RowSelectionState | null>,
  toastHighlightExpansionRef?: MutableRefObject<any | null>
): ColumnDef<any> => {
  const displayTitle = getLocalizationMap()[enumObjClassNames.box]?.[boxObjClassFieldIds.NAME];
  if (!displayTitle) {
    throw new Error("Localization missing for Assignments column");
  }
  return {
    ...commonDef,
    id: commonDef.id!,
    accessorFn: (row: any) => {
      if (row.subRows?.length === 1) {
        return row.subRows[0][fieldName] ?? "";
      }
      if (row.subRows?.length > 1) {
        return undefined;
      }
      return row[fieldName];
    },
    header: () => getOverflowText(displayTitle),
    size: 300,
    cell: ({ row, getValue, column }: CellContext<any, string>) => {
      if (row.getCanExpand()) {
        return `(${appstr("multiple")})`;
      }

      const value = getValue();

      if (isBlockedField(value)) {
        return getBlockedFieldDisplayValue();
      }

      const handleEmployeeClick = () => {
        const wsMgr = GetWSMgr();
        const planMgr = wsMgr?.GetPlanMgr();

        if (!planMgr) {
          return;
        }

        toastHighlightSelectionRef && (toastHighlightSelectionRef.current = null);
        toastHighlightExpansionRef && (toastHighlightExpansionRef.current = null);

        // Get employee data object from the row(handles both row and subrow)
        let rowData = row.original;
        // If the row has subRows, it's supposed to be a position row with single employee
        rowData = rowData.subRows?.length ? rowData.subRows[0] : rowData;
        const employeeObj = getObjFromRowData(rowData);
        if (employeeObj) {
          // Try to use the object directly first
          const overrideObj = planMgr.GetActiveOverrideOrCreate(employeeObj);
          planMgr.handleRecord(overrideObj, employeeObj);
        } else {
          // Fallback: load employee by ID if object creation fails
          const employeeID = rowData._id;
          if (employeeID) {
            loadEmployeeById(employeeID, (loadedEmployee: any) => {
              if (loadedEmployee) {
                const overrideObj = planMgr.GetActiveOverrideOrCreate(loadedEmployee);
                planMgr.handleRecord(overrideObj, loadedEmployee);
              }
            });
          }
        }
      };

      return renderCellValue({
        row,
        value,
        column,
        renderValue: value =>
          value ? (
            <Button
              modifier="link"
              size="small"
              onClick={handleEmployeeClick}
              color="info"
              dataTestId="planning-table-employee-name-link"
            >
              {value}
            </Button>
          ) : (
            value
          )
      });
    }
  };
};

export const getSelectedRowsCount = () => {
  const { state } = usePlanningTabularContext();
  const { rowSelection } = state;
  return Object.keys(rowSelection).length;
};

export const getIsSomeRowSelected = () => {
  return getSelectedRowsCount() > 0;
};

export const getObjFromRowData = (rowData: any) => {
  const doc = GetDoc();
  const objDb = doc?.GetObjDB();
  const objClass = objDb?.GetClassByName(rowData.dataType);

  if (!objClass) {
    return null;
  }

  const dataObj = objClass.AddNewObject();
  objClass.ProcessObject(dataObj, rowData);
  return dataObj;
};

export const getPositionsFromRowData = (rows: any[], closed?: boolean) => {
  return rows.filter(
    row =>
      row.dataType === enumObjClassNames.position && (closed ? row.__deleted__ : !row.__deleted__)
  );
};

export const handleEditPosition = (selectedRows: any[]) => {
  const wsMgr = GetWSMgr();
  if (!wsMgr) {
    return;
  }

  const planMgr = wsMgr.GetPlanMgr();
  if (!planMgr) {
    return;
  }

  const selectedPositions = getPositionsFromRowData(selectedRows);

  if (selectedPositions.length === 0) {
    return;
  }

  // single pos edit
  if (selectedPositions.length === 1) {
    const selectedPosition = selectedPositions[0];
    const dataObj = getObjFromRowData(selectedPosition);
    if (!dataObj) {
      return;
    }
    planMgr.HandleEditModalOpen(dataObj, enumOverrideType.change);
  } else {
    // multi pos edit
    const dataObjects = selectedPositions
      .map(position => getObjFromRowData(position))
      .filter(obj => obj !== null);

    if (dataObjects.length === 0) {
      return;
    }

    planMgr.HandleEditMultiModalOpen(dataObjects, enumOverrideType.change);
  }
};

export const handleCreatePosition = (selectedRows: any[], isClone: boolean = false) => {
  const wsMgr = GetWSMgr();
  if (!wsMgr) {
    return;
  }

  const planMgr = wsMgr.GetPlanMgr();
  if (!planMgr) {
    return;
  }

  const selectedPositions = selectedRows.filter(row => row.dataType === enumObjClassNames.position);

  if (isClone) {
    if (selectedPositions.length !== 1) {
      return;
    }

    const dataObj = getObjFromRowData(selectedPositions[0]);
    if (!dataObj) {
      return;
    }

    planMgr.handleClonePosition(dataObj);
  } else {
    if (selectedPositions.length > 0) {
      const dataObj = getObjFromRowData(selectedPositions[0]);
      planMgr.handleCreatePosition(dataObj);
    } else {
      planMgr.handleCreatePosition();
    }
  }
};

export const getSelectedPositionsWithHolders = (
  selectedPositions: any[],
  holderSelectionMap?: Record<string, any>
) => {
  return selectedPositions.map(row => {
    const subRows = row.subRows
      ? row.subRows.filter(
          (subRow: any) =>
            subRow.dataType === enumObjClassNames.box &&
            (holderSelectionMap ? holderSelectionMap[subRow._id] : true)
        )
      : row.subRows;
    return subRows
      ? {
          ...row,
          subRows
        }
      : row;
  });
};

const getPositionsWithHoldersDbObj = (selectedRows: any[]) => {
  const selectedPositions = selectedRows.filter(row => row.dataType === enumObjClassNames.position);

  if (selectedPositions.length < 1) {
    return [];
  }

  return selectedPositions
    .map(row => {
      const positionObj = getObjFromRowData(row);
      if (!positionObj) return null;

      // Get all holder sub-rows and convert to data objects
      const holders = (row.subRows || [])
        .filter((subRow: any) => subRow.dataType === enumObjClassNames.box)
        .map((holderRow: any) => getObjFromRowData(holderRow))
        .filter((obj: any) => obj !== null);

      // Set holder list on position object
      if (holders.length > 0 && positionObj.SetHolderList) {
        positionObj.SetHolderList(holders);
      }

      return positionObj;
    })
    .filter(obj => obj !== null);
};

export const hasHolders = (selectedRows: any[]): boolean => {
  return selectedRows.some(row => row.subRows && row.subRows.length > 0);
};

export const isPartialHolderSelection = (
  selectedPositions: any[],
  holderSelectionMap?: Record<string, any>
): boolean => {
  return selectedPositions.some(row => {
    const subRows = row.subRows;
    if (!holderSelectionMap || !subRows?.length) return false;
    return subRows.some((subRow: any) => !holderSelectionMap[subRow._id]);
  });
};

export const handleUnassignEmployee = (
  selectedRows: any[],
  holderSelectionMap?: Record<string, any>
) => {
  const selectedPositions = getPositionsFromRowData(selectedRows);

  const positionsWithHoldersToConsider = getSelectedPositionsWithHolders(
    selectedPositions,
    holderSelectionMap
  );
  const dataObjects = getPositionsWithHoldersDbObj(positionsWithHoldersToConsider);

  if (dataObjects.length === 0) {
    return;
  }

  promptToUnassignEmployees(
    dataObjects.length === 1 ? dataObjects[0]! : dataObjects,
    isPartialHolderSelection(selectedPositions, holderSelectionMap)
  );
};

export const handleReleaseEmployee = (
  selectedRows: any[],
  holderSelectionMap?: Record<string, any>
) => {
  const selectedPositions = getPositionsFromRowData(selectedRows);
  const positionsWithHoldersToConsider = getSelectedPositionsWithHolders(
    selectedPositions,
    holderSelectionMap
  );
  const dataObjects = getPositionsWithHoldersDbObj(positionsWithHoldersToConsider);

  if (dataObjects.length === 0) {
    return;
  }

  promptToReleaseEmployees(
    dataObjects.length === 1 ? dataObjects[0]! : dataObjects,
    isPartialHolderSelection(selectedPositions, holderSelectionMap)
  );
};

export const handleClosePosition = (selectedRows: any[]) => {
  // consider positions even if not all holders are selected
  const selectedPositions = getPositionsFromRowData(selectedRows);
  const dataObjects = getPositionsWithHoldersDbObj(selectedPositions);

  if (dataObjects.length === 0) {
    return;
  }

  promptToClosePosition(dataObjects);
};

export const handleReopenPosition = (selectedRows: any[]) => {
  const closedPositions = getPositionsFromRowData(selectedRows, true);
  const dataObjects = closedPositions
    .map(position => getObjFromRowData(position))
    .filter(obj => obj !== null);

  if (dataObjects.length === 0) {
    return;
  }

  promptToReopenPosition(dataObjects);
};

/**
 * Gets the page offset based on the current page index and page size.
 */
export const getPageOffset = (pageIndex: number, pageSize: number): number => pageIndex * pageSize;

export const getFieldInfoFromTempColField = (field: string) => {
  const doc = GetDoc();
  const SEPARATOR = "_";
  const fieldId = field.split(SEPARATOR)[0];
  const fieldClassName = field.split(SEPARATOR)[1] ?? enumObjClassNames.position;
  const objClass = doc?.GetObjDB()?.GetClassByName(fieldClassName);
  const fieldName = objClass?.GetFieldDefByID(fieldId)?.GetFieldName?.();

  return { fieldName, fieldId, fieldClassName };
};
